import { useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../components/ui/sheet';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Plus, Pencil, Trash2, Search, MoreHorizontal, ChevronDown, ChevronRight, Users, Copy, DollarSign, Truck } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';

const PriceListsTab = ({ 
  api, 
  priceLists, 
  equipmentTypes, 
  attachmentTypes, 
  workerTypes, 
  taxSettings,
  fetchPriceLists 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPriceList, setEditingPriceList] = useState(null);
  const [form, setForm] = useState({
    name: '',
    description: '',
    is_default: false,
    is_active: true
  });

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedPriceList, setSelectedPriceList] = useState(null);

  // Item dialog state
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [selectedPriceListForItem, setSelectedPriceListForItem] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [itemForm, setItemForm] = useState({
    account_item_id: '',
    equipment_type: '',
    attachment_type_ids: [],
    rate_type: 'base',
    price: '',
    uom: 'HOUR',
    min_hire: '1',
    description: ''
  });

  // Worker rate dialog state
  const [workerRateDialogOpen, setWorkerRateDialogOpen] = useState(false);
  const [selectedPriceListForRate, setSelectedPriceListForRate] = useState(null);
  const [editingWorkerRate, setEditingWorkerRate] = useState(null);
  const [workerRateForm, setWorkerRateForm] = useState({
    worker_type_id: '',
    account_item_id: '',
    rate_type: 'base',
    rate: '',
    uom: 'HOUR',
    min_hire: '1'
  });

  // Delete confirmation
  const [deleteItemConfirm, setDeleteItemConfirm] = useState({ open: false, priceList: null, item: null });

  // Expanded states
  const [expandedEquipmentTypes, setExpandedEquipmentTypes] = useState({});
  const [expandedWorkerTypes, setExpandedWorkerTypes] = useState({});

  const filteredPriceLists = priceLists.filter(pl =>
    pl.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Toggle functions
  const toggleEquipmentTypeExpand = (priceListId, equipmentType) => {
    const key = `${priceListId}_${equipmentType}`;
    setExpandedEquipmentTypes(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const toggleWorkerTypeExpand = (priceListId, workerTypeId) => {
    const key = `${priceListId}_${workerTypeId}`;
    setExpandedWorkerTypes(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const isEquipmentTypeExpanded = (priceListId, equipmentType) => {
    const key = `${priceListId}_${equipmentType}`;
    return expandedEquipmentTypes[key] ?? true;
  };

  const isWorkerTypeExpanded = (priceListId, workerTypeId) => {
    const key = `${priceListId}_${workerTypeId}`;
    return expandedWorkerTypes[key] ?? true;
  };

  // Group functions
  const groupItemsByEquipmentType = (items) => {
    if (!items || items.length === 0) return {};
    
    const grouped = {};
    items.forEach(item => {
      const eqTypeId = item.equipment_type_id || item.equipment_type;
      // Look up equipment type by ID first, then by name for legacy data
      let eqTypeName = equipmentTypes.find(t => t.id === eqTypeId)?.name;
      if (!eqTypeName) {
        // Try matching by name (for legacy/seed data)
        eqTypeName = equipmentTypes.find(t => t.name?.toLowerCase() === eqTypeId?.toLowerCase())?.name || eqTypeId || 'Other';
      }
      if (!grouped[eqTypeName]) {
        grouped[eqTypeName] = [];
      }
      grouped[eqTypeName].push(item);
    });
    
    const sortedGrouped = {};
    Object.keys(grouped).sort().forEach(key => {
      sortedGrouped[key] = grouped[key];
    });
    
    return sortedGrouped;
  };

  const groupRatesByWorkerType = (rates) => {
    if (!rates || rates.length === 0) return {};
    
    const grouped = {};
    rates.forEach(rate => {
      const wtId = rate.worker_type_id;
      if (!grouped[wtId]) {
        grouped[wtId] = [];
      }
      grouped[wtId].push(rate);
    });
    
    const rateTypeOrder = { base: 0, overtime: 1, travel: 2 };
    Object.keys(grouped).forEach(key => {
      grouped[key].sort((a, b) => {
        const orderA = rateTypeOrder[a.rate_type || 'base'] ?? 3;
        const orderB = rateTypeOrder[b.rate_type || 'base'] ?? 3;
        return orderA - orderB;
      });
    });
    
    return grouped;
  };

  // Price List handlers
  const resetForm = () => {
    setForm({ name: '', description: '', is_default: false, is_active: true });
    setEditingPriceList(null);
  };

  const handleOpenDialog = (priceList = null) => {
    if (priceList) {
      setEditingPriceList(priceList);
      setForm({
        name: priceList.name,
        description: priceList.description || '',
        is_default: priceList.is_default,
        is_active: priceList.is_active
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPriceList) {
        await api.put(`/price-lists/${editingPriceList.id}`, form);
        toast.success('Price list updated');
      } else {
        await api.post('/price-lists', form);
        toast.success('Price list created');
      }
      setDialogOpen(false);
      resetForm();
      fetchPriceLists();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (priceList) => {
    if (!window.confirm(`Delete price list "${priceList.name}"?`)) return;
    try {
      await api.delete(`/price-lists/${priceList.id}`);
      toast.success('Price list deleted');
      if (selectedPriceList?.id === priceList.id) {
        setDrawerOpen(false);
        setSelectedPriceList(null);
      }
      fetchPriceLists();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    }
  };

  const handleDuplicate = async (priceList) => {
    try {
      const response = await api.post(`/price-lists/${priceList.id}/duplicate`);
      toast.success(`Created "${response.data.name}"`);
      fetchPriceLists();
      handleOpenDrawer(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to duplicate price list');
    }
  };

  const handleOpenDrawer = (priceList) => {
    setSelectedPriceList(priceList);
    setDrawerOpen(true);
  };

  const refreshSelectedPriceList = async () => {
    if (selectedPriceList) {
      try {
        const response = await api.get(`/price-lists/${selectedPriceList.id}`);
        setSelectedPriceList(response.data);
      } catch (error) {
        console.error('Failed to refresh price list:', error);
      }
    }
  };

  // Item handlers
  const resetItemForm = () => {
    setItemForm({
      account_item_id: '',
      equipment_type: '',
      attachment_type_ids: [],
      rate_type: 'base',
      price: '',
      uom: 'HOUR',
      min_hire: '1',
      description: ''
    });
    setEditingItem(null);
  };

  const handleOpenItemDialog = (priceList, item = null, preSelectedEquipmentType = null) => {
    setSelectedPriceListForItem(priceList);
    if (item) {
      setEditingItem(item);
      setItemForm({
        account_item_id: item.account_item_id || '',
        equipment_type: item.equipment_type_id || item.equipment_type || '',
        attachment_type_ids: item.attachment_type_ids || [],
        rate_type: item.rate_type || 'base',
        price: (item.rate || item.price || '').toString(),
        uom: item.uom || 'HOUR',
        min_hire: item.min_hire?.toString() || '1',
        description: item.description || ''
      });
    } else {
      setEditingItem(null);
      setItemForm({
        account_item_id: '',
        equipment_type: preSelectedEquipmentType || '',
        attachment_type_ids: [],
        rate_type: 'base',
        price: '',
        uom: 'HOUR',
        min_hire: '1',
        description: ''
      });
    }
    setItemDialogOpen(true);
  };

  const getUsedRateTypes = (equipmentTypeId) => {
    if (!selectedPriceListForItem?.items || !equipmentTypeId) return [];
    
    const equipmentTypeName = equipmentTypes.find(t => t.id === equipmentTypeId)?.name;
    
    const used = selectedPriceListForItem.items
      .filter(item => {
        const itemEqTypeId = item.equipment_type_id || item.equipment_type;
        const matches = itemEqTypeId === equipmentTypeId || itemEqTypeId === equipmentTypeName;
        return matches && (!editingItem || item.id !== editingItem.id);
      })
      .map(item => item.rate_type || 'base');
    
    return used;
  };

  const getFilteredAttachmentTypesForEquipment = (equipmentTypeId) => {
    if (!equipmentTypeId) return [];
    
    const eqType = equipmentTypes.find(et => et.id === equipmentTypeId || et.name === equipmentTypeId);
    if (!eqType) return [];
    
    return attachmentTypes.filter(at => 
      at.is_active && (
        !at.equipment_type_ids || 
        at.equipment_type_ids.length === 0 || 
        at.equipment_type_ids.includes(eqType.id)
      )
    );
  };

  const handleItemSubmit = async (e) => {
    e.preventDefault();
    
    const usedRateTypes = getUsedRateTypes(itemForm.equipment_type);
    if (!editingItem && usedRateTypes.includes(itemForm.rate_type)) {
      const eqTypeName = equipmentTypes.find(t => t.id === itemForm.equipment_type)?.name || itemForm.equipment_type;
      toast.error(`A ${itemForm.rate_type} rate for ${eqTypeName} already exists in this price list`);
      return;
    }
    
    const itemData = {
      equipment_type_id: itemForm.equipment_type || null,
      account_item_id: itemForm.account_item_id || null,
      description: itemForm.description || null,
      rate: parseFloat(itemForm.price),
      rate_type: itemForm.rate_type,
      uom: itemForm.uom,
      min_hire: parseFloat(itemForm.min_hire)
    };
    try {
      if (editingItem) {
        await api.put(`/price-lists/${selectedPriceListForItem.id}/items/${editingItem.id}`, itemData);
        toast.success('Item updated');
      } else {
        await api.post(`/price-lists/${selectedPriceListForItem.id}/items`, itemData);
        toast.success('Item added');
      }
      setItemDialogOpen(false);
      resetItemForm();
      fetchPriceLists();
      refreshSelectedPriceList();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        toast.error(detail.map(e => e.msg).join(', '));
      } else {
        toast.error(detail || 'Operation failed');
      }
    }
  };

  const handleItemDelete = async (priceList, item) => {
    setDeleteItemConfirm({ open: true, priceList, item });
  };

  const confirmItemDelete = async () => {
    const { priceList, item } = deleteItemConfirm;
    if (!priceList || !item) return;
    
    try {
      await api.delete(`/price-lists/${priceList.id}/items/${item.id}`);
      toast.success('Item deleted');
      fetchPriceLists();
      refreshSelectedPriceList();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        toast.error(detail.map(e => e.msg).join(', '));
      } else {
        toast.error(detail || 'Delete failed');
      }
    } finally {
      setDeleteItemConfirm({ open: false, priceList: null, item: null });
    }
  };

  // Worker rate handlers
  const resetWorkerRateForm = () => {
    setWorkerRateForm({
      worker_type_id: '',
      account_item_id: '',
      rate_type: 'base',
      rate: '',
      uom: 'HOUR',
      min_hire: '1'
    });
    setEditingWorkerRate(null);
  };

  const getAvailableRateTypesForWorker = (priceList, workerTypeId, editingRateType = null) => {
    const allTypes = ['base', 'overtime', 'travel'];
    if (!priceList?.worker_type_rates || !workerTypeId) return allTypes;
    
    const usedTypes = priceList.worker_type_rates
      .filter(r => r.worker_type_id === workerTypeId)
      .map(r => r.rate_type || 'base');
    
    return allTypes.filter(t => !usedTypes.includes(t) || t === editingRateType);
  };

  const handleOpenWorkerRateDialog = (priceList, rate = null) => {
    setSelectedPriceListForRate(priceList);
    if (rate) {
      setEditingWorkerRate(rate);
      setWorkerRateForm({
        worker_type_id: rate.worker_type_id,
        account_item_id: rate.account_item_id || '',
        rate_type: rate.rate_type || 'base',
        rate: rate.rate.toString(),
        uom: rate.uom || 'HOUR',
        min_hire: rate.min_hire?.toString() || '1'
      });
    } else {
      resetWorkerRateForm();
    }
    setWorkerRateDialogOpen(true);
  };

  const handleWorkerRateSubmit = async (e) => {
    e.preventDefault();
    const rateData = {
      worker_type_id: workerRateForm.worker_type_id,
      account_item_id: workerRateForm.account_item_id || null,
      rate_type: workerRateForm.rate_type,
      rate: parseFloat(workerRateForm.rate),
      uom: workerRateForm.uom,
      min_hire: parseFloat(workerRateForm.min_hire)
    };
    try {
      if (editingWorkerRate) {
        await api.put(`/price-lists/${selectedPriceListForRate.id}/worker-rates/${editingWorkerRate.id}`, rateData);
        toast.success('Worker rate updated');
      } else {
        await api.post(`/price-lists/${selectedPriceListForRate.id}/worker-rates`, rateData);
        toast.success('Worker rate added');
      }
      setWorkerRateDialogOpen(false);
      resetWorkerRateForm();
      fetchPriceLists();
      refreshSelectedPriceList();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleWorkerRateDelete = async (priceList, rate) => {
    if (!window.confirm('Delete this worker type rate?')) return;
    try {
      await api.delete(`/price-lists/${priceList.id}/worker-rates/${rate.id}`);
      toast.success('Worker rate deleted');
      fetchPriceLists();
      refreshSelectedPriceList();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <>
      <Card className="card-industrial">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <CardTitle className="font-heading">Price Lists</CardTitle>
              <CardDescription>
                {priceLists.filter(p => p.is_active).length} active price lists
              </CardDescription>
            </div>
            <Button className="btn-volt" onClick={() => handleOpenDialog()} data-testid="add-price-list-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Price List
            </Button>
          </div>
          <div className="relative max-w-sm mt-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              data-testid="price-list-search"
              placeholder="Search price lists..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
        </CardHeader>
        <CardContent>
          {/* Tax Exclusion Message */}
          <div className="bg-muted/50 border rounded-lg p-4 flex items-start gap-3 mb-6">
            <DollarSign className="h-5 w-5 text-[#ffd108] shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium">
                {taxSettings.tax_name === 'None' 
                  ? 'Prices shown are final (no tax applied)'
                  : `All prices are exclusive of ${taxSettings.tax_name} (${taxSettings.tax_rate}%)`
                }
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Tax settings can be changed in Settings → Organization
              </p>
            </div>
          </div>
          
          {filteredPriceLists.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No price lists found. Create a price list to set rates for equipment and workers.
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="label-overline">Name</TableHead>
                    <TableHead className="label-overline">Description</TableHead>
                    <TableHead className="label-overline text-center">Asset Items</TableHead>
                    <TableHead className="label-overline text-center">Worker Rates</TableHead>
                    <TableHead className="label-overline text-center">Status</TableHead>
                    <TableHead className="label-overline text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPriceLists.map((priceList) => (
                    <TableRow 
                      key={priceList.id} 
                      className="cursor-pointer hover:bg-muted/50"
                      data-testid={`price-list-row-${priceList.id}`}
                      onClick={() => handleOpenDrawer(priceList)}
                    >
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <span>{priceList.name}</span>
                          {priceList.is_default && (
                            <Badge className="bg-[#ffd108] text-black text-xs">Default</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm max-w-[200px] truncate">
                        {priceList.description || '-'}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline">{priceList.items?.length || 0}</Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline">{priceList.worker_type_rates?.length || 0}</Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        {priceList.is_active ? (
                          <Badge className="bg-green-600">Active</Badge>
                        ) : (
                          <Badge variant="secondary">Inactive</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleOpenDialog(priceList); }}>
                              <Pencil className="h-4 w-4 mr-2" />
                              Edit Details
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleDuplicate(priceList); }}>
                              <Copy className="h-4 w-4 mr-2" />
                              Duplicate
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={(e) => { e.stopPropagation(); handleDelete(priceList); }} 
                              className="text-destructive"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Price List Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingPriceList ? 'Edit Price List' : 'Add Price List'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="service-form">
            <div className="space-y-2">
              <Label className="label-overline">Name *</Label>
              <Input
                data-testid="price-list-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g., Default Price List"
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="label-overline">Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Brief description"
                rows={2}
              />
            </div>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-3">
                <Switch
                  checked={form.is_default}
                  onCheckedChange={(checked) => setForm({ ...form, is_default: checked })}
                />
                <Label>Default</Label>
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  checked={form.is_active}
                  onCheckedChange={(checked) => setForm({ ...form, is_active: checked })}
                />
                <Label>Active</Label>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-volt" data-testid="save-service-btn">
                {editingPriceList ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Price List Detail Drawer */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent side="right" className="w-[900px] sm:max-w-[900px] overflow-y-auto">
          {selectedPriceList && (
            <>
              <SheetHeader className="pb-4 border-b">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <DollarSign className="h-6 w-6 text-[#ffd108]" />
                    <div>
                      <SheetTitle className="font-heading text-xl flex items-center gap-2">
                        {selectedPriceList.name}
                        {selectedPriceList.is_default && (
                          <Badge className="bg-[#ffd108] text-black">Default</Badge>
                        )}
                        {!selectedPriceList.is_active && (
                          <Badge variant="secondary">Inactive</Badge>
                        )}
                      </SheetTitle>
                      {selectedPriceList.description && (
                        <p className="text-sm text-muted-foreground mt-1">
                          {selectedPriceList.description}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleDuplicate(selectedPriceList)}
                    >
                      <Copy className="h-4 w-4 mr-2" />
                      Duplicate
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleOpenDialog(selectedPriceList)}
                    >
                      <Pencil className="h-4 w-4 mr-2" />
                      Edit Details
                    </Button>
                  </div>
                </div>
              </SheetHeader>

              <div className="py-6 space-y-8">
                {/* Equipment Items Section */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Truck className="h-5 w-5 text-[#ffd108]" />
                      <h3 className="font-heading font-semibold text-lg">Asset Rates</h3>
                      <Badge variant="secondary">
                        {selectedPriceList.items?.length || 0} items
                      </Badge>
                    </div>
                    <Button 
                      className="btn-volt" 
                      size="sm"
                      onClick={() => handleOpenItemDialog(selectedPriceList)}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Item
                    </Button>
                  </div>

                  {selectedPriceList.items?.length > 0 ? (
                    <div className="space-y-3">
                      {Object.entries(groupItemsByEquipmentType(selectedPriceList.items)).map(([equipmentType, items]) => {
                        const isExpanded = isEquipmentTypeExpanded(selectedPriceList.id, equipmentType);
                        return (
                          <div key={equipmentType} className="border rounded-lg overflow-hidden">
                            <div
                              onClick={() => toggleEquipmentTypeExpand(selectedPriceList.id, equipmentType)}
                              className="w-full flex items-center justify-between p-3 bg-muted/50 hover:bg-muted transition-colors cursor-pointer"
                            >
                              <div className="flex items-center gap-3">
                                {isExpanded ? (
                                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                ) : (
                                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                )}
                                <Truck className="h-4 w-4 text-[#ffd108]" />
                                <span className="font-semibold">{equipmentType}</span>
                                <Badge variant="secondary" className="text-xs">
                                  {items.length} item{items.length !== 1 ? 's' : ''}
                                </Badge>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-muted-foreground">
                                  From {taxSettings.currency_symbol}{Math.min(...items.map(i => i.rate || i.price || 0)).toFixed(2)}
                                </span>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 px-2 text-xs"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const eqTypeId = equipmentTypes.find(t => t.name === equipmentType)?.id || equipmentType;
                                    handleOpenItemDialog(selectedPriceList, null, eqTypeId);
                                  }}
                                >
                                  <Plus className="h-3 w-3 mr-1" />
                                  Add
                                </Button>
                              </div>
                            </div>
                            
                            {isExpanded && (
                              <Table>
                                <TableHeader>
                                  <TableRow className="bg-background">
                                    <TableHead className="label-overline pl-10">Account ID</TableHead>
                                    <TableHead className="label-overline">Rate Type</TableHead>
                                    <TableHead className="label-overline">Attachments</TableHead>
                                    <TableHead className="label-overline text-right">Price</TableHead>
                                    <TableHead className="label-overline">UOM</TableHead>
                                    <TableHead className="label-overline">Min</TableHead>
                                    <TableHead className="label-overline text-right">Actions</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {items.map((item) => (
                                    <TableRow key={item.id} className="hover:bg-muted/30">
                                      <TableCell className="font-mono text-xs pl-10">
                                        {item.account_item_id || '-'}
                                      </TableCell>
                                      <TableCell>
                                        <Badge 
                                          variant={(!item.rate_type || item.rate_type === 'base') ? 'default' : 'outline'}
                                          className={
                                            item.rate_type === 'overtime' ? 'border-orange-500 text-orange-600' :
                                            item.rate_type === 'travel' ? 'border-blue-500 text-blue-600' : ''
                                          }
                                        >
                                          {(!item.rate_type || item.rate_type === 'base') ? 'Base' : 
                                           item.rate_type === 'overtime' ? 'Overtime' : 
                                           item.rate_type === 'travel' ? 'Travel' : item.rate_type}
                                        </Badge>
                                      </TableCell>
                                      <TableCell className="text-sm">
                                        {item.attachment_type_ids?.length > 0 ? (
                                          <div className="flex flex-wrap gap-1">
                                            {item.attachment_type_ids.map(id => (
                                              <Badge key={id} variant="outline" className="text-xs">
                                                {attachmentTypes.find(t => t.id === id)?.name || id}
                                              </Badge>
                                            ))}
                                          </div>
                                        ) : (
                                          <span className="text-muted-foreground italic">-</span>
                                        )}
                                      </TableCell>
                                      <TableCell className="font-mono font-semibold text-[#ffd108] text-right">
                                        {taxSettings.currency_symbol}{(item.rate || item.price || 0).toFixed(2)}
                                      </TableCell>
                                      <TableCell>{item.uom}</TableCell>
                                      <TableCell>{item.min_hire}</TableCell>
                                      <TableCell className="text-right">
                                        <DropdownMenu>
                                          <DropdownMenuTrigger asChild>
                                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                              <MoreHorizontal className="h-4 w-4" />
                                            </Button>
                                          </DropdownMenuTrigger>
                                          <DropdownMenuContent align="end">
                                            <DropdownMenuItem onSelect={() => handleOpenItemDialog(selectedPriceList, item)}>
                                              <Pencil className="h-4 w-4 mr-2" />
                                              Edit
                                            </DropdownMenuItem>
                                            <DropdownMenuSeparator />
                                            <DropdownMenuItem 
                                              onSelect={() => handleItemDelete(selectedPriceList, item)}
                                              className="text-destructive focus:text-destructive"
                                            >
                                              <Trash2 className="h-4 w-4 mr-2" />
                                              Delete
                                            </DropdownMenuItem>
                                          </DropdownMenuContent>
                                        </DropdownMenu>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground border rounded-lg">
                      <Truck className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>No equipment rates yet.</p>
                      <button 
                        onClick={() => handleOpenItemDialog(selectedPriceList)} 
                        className="text-[#ffd108] hover:underline mt-1"
                      >
                        Add your first equipment rate
                      </button>
                    </div>
                  )}
                </div>

                {/* Worker Type Rates Section */}
                <div className="border-t pt-8">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Users className="h-5 w-5 text-[#ffd108]" />
                      <h3 className="font-heading font-semibold text-lg">Worker Rates</h3>
                      <Badge variant="secondary">
                        {selectedPriceList.worker_type_rates?.length || 0} rates
                      </Badge>
                    </div>
                    <Button 
                      className="btn-volt" 
                      size="sm"
                      onClick={() => handleOpenWorkerRateDialog(selectedPriceList)}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Rate
                    </Button>
                  </div>

                  {selectedPriceList.worker_type_rates?.length > 0 ? (
                    <div className="space-y-3">
                      {Object.entries(groupRatesByWorkerType(selectedPriceList.worker_type_rates)).map(([workerTypeId, rates]) => {
                        const workerType = workerTypes.find(wt => wt.id === workerTypeId);
                        const isExpanded = isWorkerTypeExpanded(selectedPriceList.id, workerTypeId);
                        const usedRateTypes = rates.map(r => r.rate_type || 'base');
                        const canAddMore = usedRateTypes.length < 3;
                        
                        return (
                          <div key={workerTypeId} className="border rounded-lg overflow-hidden">
                            <div
                              onClick={() => toggleWorkerTypeExpand(selectedPriceList.id, workerTypeId)}
                              className="w-full flex items-center justify-between p-3 bg-muted/50 hover:bg-muted transition-colors cursor-pointer"
                            >
                              <div className="flex items-center gap-3">
                                {isExpanded ? (
                                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                ) : (
                                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                )}
                                <Users className="h-4 w-4 text-[#ffd108]" />
                                <span className="font-semibold">{workerType?.name || workerTypeId}</span>
                                <Badge variant="secondary" className="text-xs">
                                  {rates.length} rate{rates.length !== 1 ? 's' : ''}
                                </Badge>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-muted-foreground">
                                  Base: {taxSettings.currency_symbol}{rates.find(r => !r.rate_type || r.rate_type === 'base')?.rate?.toFixed(2) || '-'}
                                </span>
                                {canAddMore && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 px-2 text-xs"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setWorkerRateForm(prev => ({ ...prev, worker_type_id: workerTypeId }));
                                      handleOpenWorkerRateDialog(selectedPriceList);
                                    }}
                                  >
                                    <Plus className="h-3 w-3 mr-1" />
                                    Add
                                  </Button>
                                )}
                              </div>
                            </div>
                            
                            {isExpanded && (
                              <Table>
                                <TableHeader>
                                  <TableRow className="bg-background">
                                    <TableHead className="label-overline pl-10">Account ID</TableHead>
                                    <TableHead className="label-overline">Rate Type</TableHead>
                                    <TableHead className="label-overline text-right">Price</TableHead>
                                    <TableHead className="label-overline">UOM</TableHead>
                                    <TableHead className="label-overline">Min</TableHead>
                                    <TableHead className="label-overline text-right">Actions</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {rates.map((rate) => (
                                    <TableRow key={rate.id} className="hover:bg-muted/30">
                                      <TableCell className="font-mono text-xs pl-10">
                                        {rate.account_item_id || '-'}
                                      </TableCell>
                                      <TableCell>
                                        <Badge 
                                          variant={(!rate.rate_type || rate.rate_type === 'base') ? 'default' : 'outline'}
                                          className={
                                            rate.rate_type === 'overtime' ? 'border-orange-500 text-orange-600' :
                                            rate.rate_type === 'travel' ? 'border-blue-500 text-blue-600' : ''
                                          }
                                        >
                                          {(!rate.rate_type || rate.rate_type === 'base') ? 'Base' : 
                                           rate.rate_type === 'overtime' ? 'Overtime' : 
                                           rate.rate_type === 'travel' ? 'Travel' : rate.rate_type}
                                        </Badge>
                                      </TableCell>
                                      <TableCell className="font-mono font-semibold text-[#ffd108] text-right">
                                        {taxSettings.currency_symbol}{rate.rate.toFixed(2)}
                                      </TableCell>
                                      <TableCell>{rate.uom || 'HOUR'}</TableCell>
                                      <TableCell>{rate.min_hire || 1}</TableCell>
                                      <TableCell className="text-right">
                                        <DropdownMenu>
                                          <DropdownMenuTrigger asChild>
                                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                              <MoreHorizontal className="h-4 w-4" />
                                            </Button>
                                          </DropdownMenuTrigger>
                                          <DropdownMenuContent align="end">
                                            <DropdownMenuItem onClick={() => handleOpenWorkerRateDialog(selectedPriceList, rate)}>
                                              <Pencil className="h-4 w-4 mr-2" />
                                              Edit
                                            </DropdownMenuItem>
                                            <DropdownMenuSeparator />
                                            <DropdownMenuItem 
                                              onClick={() => handleWorkerRateDelete(selectedPriceList, rate)}
                                              className="text-destructive focus:text-destructive"
                                            >
                                              <Trash2 className="h-4 w-4 mr-2" />
                                              Delete
                                            </DropdownMenuItem>
                                          </DropdownMenuContent>
                                        </DropdownMenu>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground border rounded-lg">
                      <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>No worker rates yet.</p>
                      <button 
                        onClick={() => handleOpenWorkerRateDialog(selectedPriceList)} 
                        className="text-[#ffd108] hover:underline mt-1"
                      >
                        Add your first worker rate
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Add/Edit Price List Item Dialog */}
      <Dialog open={itemDialogOpen} onOpenChange={setItemDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingItem ? 'Edit Price Item' : 'Add Price Item'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleItemSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="label-overline">Account Item ID</Label>
                <Input
                  value={itemForm.account_item_id}
                  onChange={(e) => setItemForm({ ...itemForm, account_item_id: e.target.value })}
                  placeholder="XERO Item Code"
                  className="font-mono"
                />
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Asset Type *</Label>
                <Select
                  value={itemForm.equipment_type}
                  onValueChange={(v) => {
                    const compatibleAttachments = getFilteredAttachmentTypesForEquipment(v);
                    const validAttachmentIds = itemForm.attachment_type_ids.filter(id =>
                      compatibleAttachments.some(at => at.id === id)
                    );
                    const usedRates = getUsedRateTypes(v);
                    const availableRateType = ['base', 'overtime', 'travel'].find(rt => !usedRates.includes(rt)) || 'base';
                    setItemForm({ 
                      ...itemForm, 
                      equipment_type: v,
                      attachment_type_ids: validAttachmentIds,
                      rate_type: usedRates.includes(itemForm.rate_type) ? availableRateType : itemForm.rate_type
                    });
                  }}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {equipmentTypes.filter(t => t.is_active).map((type) => (
                      <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {itemForm.equipment_type && getFilteredAttachmentTypesForEquipment(itemForm.equipment_type).length > 0 && (
              <div className="space-y-2">
                <Label className="label-overline">Attachment Types (Optional)</Label>
                <p className="text-xs text-muted-foreground">
                  Showing attachments compatible with {equipmentTypes.find(t => t.id === itemForm.equipment_type)?.name || itemForm.equipment_type}
                </p>
                <div className="border rounded-md p-3 max-h-28 overflow-y-auto space-y-2">
                  {getFilteredAttachmentTypesForEquipment(itemForm.equipment_type).map((type) => (
                    <div key={type.id} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id={`item-att-${type.id}`}
                        checked={itemForm.attachment_type_ids.includes(type.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setItemForm({
                              ...itemForm,
                              attachment_type_ids: [...itemForm.attachment_type_ids, type.id]
                            });
                          } else {
                            setItemForm({
                              ...itemForm,
                              attachment_type_ids: itemForm.attachment_type_ids.filter(id => id !== type.id)
                            });
                          }
                        }}
                        className="rounded"
                      />
                      <label htmlFor={`item-att-${type.id}`} className="text-sm cursor-pointer">
                        {type.name}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <Label className="label-overline">Rate Type *</Label>
              {(() => {
                const usedRateTypes = getUsedRateTypes(itemForm.equipment_type);
                const allRateTypes = [
                  { value: 'base', label: 'Base Rate' },
                  { value: 'overtime', label: 'Overtime' },
                  { value: 'travel', label: 'Travel' }
                ];
                return (
                  <Select
                    value={itemForm.rate_type}
                    onValueChange={(v) => setItemForm({ ...itemForm, rate_type: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {allRateTypes.map(rt => (
                        <SelectItem 
                          key={rt.value} 
                          value={rt.value}
                          disabled={usedRateTypes.includes(rt.value)}
                        >
                          {rt.label}{usedRateTypes.includes(rt.value) ? ' (already added)' : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                );
              })()}
              <p className="text-xs text-muted-foreground">
                {itemForm.rate_type === 'base' && 'Standard hourly/daily rate for this equipment'}
                {itemForm.rate_type === 'overtime' && 'Rate charged for overtime hours'}
                {itemForm.rate_type === 'travel' && 'Rate charged for travel time'}
              </p>
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="label-overline">Price *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={itemForm.price}
                  onChange={(e) => setItemForm({ ...itemForm, price: e.target.value })}
                  placeholder="0.00"
                  className="font-mono"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label className="label-overline">UOM</Label>
                <Select
                  value={itemForm.uom}
                  onValueChange={(v) => setItemForm({ ...itemForm, uom: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="HOUR">HOUR</SelectItem>
                    <SelectItem value="DAY">DAY</SelectItem>
                    <SelectItem value="WEEK">WEEK</SelectItem>
                    <SelectItem value="EACH">EACH</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Min Hire</Label>
                <Input
                  type="number"
                  step="0.5"
                  min="0"
                  value={itemForm.min_hire}
                  onChange={(e) => setItemForm({ ...itemForm, min_hire: e.target.value })}
                  placeholder="1"
                  className="font-mono"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setItemDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-volt" disabled={!itemForm.equipment_type || !itemForm.price}>
                {editingItem ? 'Update' : 'Add Item'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Worker Type Rate Dialog */}
      <Dialog open={workerRateDialogOpen} onOpenChange={setWorkerRateDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingWorkerRate ? 'Edit Worker Rate' : 'Add Worker Rate'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleWorkerRateSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="label-overline">Account Item ID</Label>
              <Input
                value={workerRateForm.account_item_id}
                onChange={(e) => setWorkerRateForm({ ...workerRateForm, account_item_id: e.target.value })}
                placeholder="e.g., LABOUR-001"
                className="font-mono"
              />
              <p className="text-xs text-muted-foreground">
                Optional: Link to your accounting software (Xero)
              </p>
            </div>
            
            <div className="space-y-2">
              <Label className="label-overline">Worker Type *</Label>
              <Select
                value={workerRateForm.worker_type_id}
                onValueChange={(v) => {
                  const availableTypes = getAvailableRateTypesForWorker(selectedPriceListForRate, v, editingWorkerRate?.rate_type);
                  setWorkerRateForm({ 
                    ...workerRateForm, 
                    worker_type_id: v,
                    rate_type: availableTypes[0] || 'base'
                  });
                }}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select worker type" />
                </SelectTrigger>
                <SelectContent>
                  {workerTypes.map((type) => (
                    <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label className="label-overline">Rate Type *</Label>
              <Select
                value={workerRateForm.rate_type}
                onValueChange={(v) => setWorkerRateForm({ ...workerRateForm, rate_type: v })}
                disabled={!workerRateForm.worker_type_id}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select rate type" />
                </SelectTrigger>
                <SelectContent>
                  {getAvailableRateTypesForWorker(
                    selectedPriceListForRate, 
                    workerRateForm.worker_type_id, 
                    editingWorkerRate?.rate_type
                  ).map((rateType) => (
                    <SelectItem key={rateType} value={rateType}>
                      {rateType === 'base' ? 'Base Rate' : 
                       rateType === 'overtime' ? 'Overtime' : 
                       rateType === 'travel' ? 'Travel' : rateType}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {workerRateForm.worker_type_id && getAvailableRateTypesForWorker(selectedPriceListForRate, workerRateForm.worker_type_id, editingWorkerRate?.rate_type).length === 0 && (
                <p className="text-xs text-amber-600">
                  All rate types (Base, Overtime, Travel) are already configured for this worker type.
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                {workerRateForm.rate_type === 'base' && 'Standard rate for this worker type'}
                {workerRateForm.rate_type === 'overtime' && 'Rate charged for overtime hours'}
                {workerRateForm.rate_type === 'travel' && 'Rate charged for travel time'}
              </p>
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="label-overline">Price ({taxSettings.currency_symbol}) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={workerRateForm.rate}
                  onChange={(e) => setWorkerRateForm({ ...workerRateForm, rate: e.target.value })}
                  placeholder="0.00"
                  className="font-mono"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label className="label-overline">UOM</Label>
                <Select
                  value={workerRateForm.uom}
                  onValueChange={(v) => setWorkerRateForm({ ...workerRateForm, uom: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="HOUR">HOUR</SelectItem>
                    <SelectItem value="DAY">DAY</SelectItem>
                    <SelectItem value="WEEK">WEEK</SelectItem>
                    <SelectItem value="EACH">EACH</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Min Hire</Label>
                <Input
                  type="number"
                  step="0.5"
                  min="0"
                  value={workerRateForm.min_hire}
                  onChange={(e) => setWorkerRateForm({ ...workerRateForm, min_hire: e.target.value })}
                  placeholder="1"
                  className="font-mono"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setWorkerRateDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-volt" disabled={!workerRateForm.worker_type_id || !workerRateForm.rate}>
                {editingWorkerRate ? 'Update' : 'Add Rate'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Delete Item Confirmation Dialog */}
      <AlertDialog open={deleteItemConfirm.open} onOpenChange={(open) => !open && setDeleteItemConfirm({ open: false, priceList: null, item: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Price Item</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this price item? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmItemDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default PriceListsTab;
